import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Plus,
  X,
  Trash2,
  Clock,
  Calendar,
  Check,
  ClipboardList,
  Inbox,
  Loader2,
  ListFilter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  User,
  UserCheck,
  Users,
  Pencil,
  Search,
  Send,
  Pause,
  Play,
  BadgeCheck,
  CalendarDays,
  Building2,
  Mail,
  UserPlus,
  Bell,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchTasks,
  createTask,
  updateTask,
  deleteTask,
  remindTask,
  fetchRoles,
  fetchDepartments,
  fetchUsers,
} from "../../apis/tasks";
import {
  fetchCirculars,
  fetchCircular,
  createCircular,
  updateCircular,
  deleteCircular,
  markCircularRead,
  remindCircular,
  fetchCircularUnreadCount,
} from "../../apis/circulars";
import ConfirmationModal from "../../components/ConfirmationModal";
import CalendarView from "./CalendarView";
import TodosSection from "./TodosSection";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import { displayToast } from "../../utils/Toast";
import "../../styles/pages/tasks/Tasks.css";

const PAGE_SIZE = 10;

const VIEW_BY_ME = "by_me";
const VIEW_FOR_ME = "for_me";
const VIEW_ALL = "all";
const STATUS_ALL = "all";
const STATUS_PENDING = "pending";
const STATUS_AWAITING_APPROVAL = "awaiting_approval";
const STATUS_COMPLETED = "completed";

const SECTION_CALENDAR = "calendar";
const SECTION_TASKS = "tasks";
const SECTION_TODOS = "todos";
const SECTION_CIRCULARS = "circulars";

const CIRCULAR_PAGE_SIZE = 6;

const RECIPIENT_USERS = "users";
const RECIPIENT_DEPARTMENTS = "departments";
const RECIPIENT_ALL = "all";

const CIRCULAR_VIEW = "view";
const CIRCULAR_CREATE = "create";
const CIRCULAR_EDIT = "edit";

const ROLE_COLORS = [
  "#2563eb",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#f59e0b",
  "#22c55e",
  "#6366f1",
  "#ef4444",
  "#0ea5e9",
  "#14b8a6",
];

const roleColor = (id) => ROLE_COLORS[(Number(id) || 0) % ROLE_COLORS.length];

const taskSortStamp = (task) => {
  const raw =
    task?.updated_at ||
    task?.updatedAt ||
    task?.modified_at ||
    task?.modifiedAt ||
    task?.updated_on ||
    task?.created_at;
  const stamp = raw ? new Date(raw).getTime() : 0;
  return Number.isNaN(stamp) ? 0 : stamp;
};

const currentUserName = () => localStorage.getItem("user_name") || "";
const currentRoleId = () => Number(localStorage.getItem("role_id") || 0);

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.departments)) return value.departments;
  if (Array.isArray(value?.department)) return value.department;
  if (Array.isArray(value?.roles)) return value.roles;
  if (Array.isArray(value?.users)) return value.users;
  return [];
};

const userDepartmentId = (user) =>
  user?.department_id ??
  user?.department?.id ??
  user?.user_department?.department_id ??
  user?.user_department?.id ??
  user?.dept_id ??
  "";

const userDepartmentName = (user) =>
  user?.department_name ??
  (typeof user?.department === "string"
    ? user.department
    : user?.department?.name) ??
  user?.user_department?.department_name ??
  (typeof user?.user_department === "string"
    ? user.user_department
    : user?.user_department?.name) ??
  user?.dept_name ??
  user?.dept ??
  "";

const userMatchesDepartment = (user, department) => {
  const departmentId = String(department?.id ?? "");
  const selectedName = String(department?.name || "")
    .trim()
    .toLowerCase();
  const userDeptId = String(userDepartmentId(user));
  if (departmentId && userDeptId) return userDeptId === departmentId;
  const userDeptName = String(userDepartmentName(user)).trim().toLowerCase();
  return Boolean(selectedName && userDeptName && userDeptName === selectedName);
};

const userRoleId = (user) => user?.user_role?.role_id ?? user?.role_id ?? "";

const departmentMatchesName = (department, name) => {
  const selectedName = String(name || "")
    .trim()
    .toLowerCase();
  if (!selectedName) return false;
  return [department?.name, department?.department_name, department?.code]
    .map((value) =>
      String(value || "")
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean)
    .includes(selectedName);
};

const fmtPersonAndRole = (name, role) => {
  const safeName = String(name || "").trim() || "-";
  const safeRole = String(role || "").trim() || "-";
  return `${safeName}, ${safeRole}`;
};

const initialsOf = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  return "?";
};

const emptyForm = () => ({
  title: "",
  description: "",
  departmentId: "",
  departmentName: "",
  roleId: "",
  roleName: "",
  assigneeId: "",
  assigneeName: "",
  date: new Date(),
  time: null,
  onHold: false,
  isCompleted: false,
});

const emptyCircularForm = () => ({
  title: "",
  message: "",
  date: null,
  time: null,
  allDay: false,
  recipientMode: RECIPIENT_USERS,
  userIds: [],
  departmentIds: [],
});

const fmtCircularDate = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fmtCircularTime = (value) => {
  if (!value) return "";
  const [h, m] = String(value).split(":");
  const hour = Number(h);
  if (Number.isNaN(hour)) return String(value);
  const suffix = hour >= 12 ? "PM" : "AM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(display).padStart(2, "0")}:${m || "00"} ${suffix}`;
};

const toApiDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toApiTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const parseApiDate = (value) => {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseApiTime = (value) => {
  if (!value) return null;
  const [h, m] = String(value).split(":");
  if (Number.isNaN(Number(h))) return null;
  const d = new Date();
  d.setHours(Number(h), Number(m) || 0, 0, 0);
  return d;
};

const buildDeadline = (date, time) => {
  if (!time || !date) return null;
  const dt = new Date(date);
  dt.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return dt.toISOString();
};

const Tasks = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state?.view ? location.state : null;
  const [activeView, setActiveView] = useState(navState?.view || VIEW_FOR_ME);
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [departmentUsers, setDepartmentUsers] = useState([]);
  const [departmentUsersDepartmentId, setDepartmentUsersDepartmentId] =
    useState("");
  const [departmentUsersLoading, setDepartmentUsersLoading] = useState(false);
  const [circularDepartmentUsersById, setCircularDepartmentUsersById] =
    useState({});
  const [circularDepartmentLoadingById, setCircularDepartmentLoadingById] =
    useState({});

  const [departmentFilter, setDepartmentFilter] = useState(
    navState?.departmentId
      ? String(navState.departmentId)
      : navState?.roleId
        ? String(navState.roleId)
        : "",
  );
  const [departmentFilterName, setDepartmentFilterName] = useState(
    navState?.departmentName || "",
  );
  const [statusFilter, setStatusFilter] = useState(
    navState?.status || STATUS_PENDING,
  );
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingTaskSnapshot, setEditingTaskSnapshot] = useState(null);
  const [focusTaskId, setFocusTaskId] = useState(null);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [userPopupOpen, setUserPopupOpen] = useState(false);
  const submitLockRef = useRef(false);
  const taskRequestIdRef = useRef(0);
  const circularDepartmentRequestsRef = useRef({});
  const editDepartmentResolveRef = useRef(0);

  const [activeSection, setActiveSection] = useState(
    navState?.view ? SECTION_TASKS : SECTION_CALENDAR,
  );
  const [todoCreateSignal, setTodoCreateSignal] = useState(0);
  const [todoFocus, setTodoFocus] = useState(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [circulars, setCirculars] = useState([]);
  const [circularsLoading, setCircularsLoading] = useState(false);
  const [circularSearch, setCircularSearch] = useState("");
  const [circularPage, setCircularPage] = useState(1);
  const [circularForm, setCircularForm] = useState(emptyCircularForm);
  const [circularMode, setCircularMode] = useState(CIRCULAR_VIEW);
  const [circularSaving, setCircularSaving] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [selectedCircular, setSelectedCircular] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [circularToDelete, setCircularToDelete] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const addMenuRef = useRef(null);

  const filterRef = useRef(null);
  const roleMenuRef = useRef(null);

  const roleMap = useMemo(() => {
    const map = {};
    roles.forEach((r) => {
      map[r.id] = r;
    });
    return map;
  }, [roles]);

  const isAdmin = useMemo(() => {
    const roleName = String(roleMap[currentRoleId()]?.name || "").toLowerCase();
    return roleName === "admin";
  }, [roleMap]);

  const findRoleByDepartmentName = useCallback(
    (departmentName) => {
      const selectedName = String(departmentName || "")
        .trim()
        .toLowerCase();
      if (!selectedName) return null;
      return roles.find(
        (role) =>
          String(role?.name || "")
            .trim()
            .toLowerCase() === selectedName,
      );
    },
    [roles],
  );

  const departmentMap = useMemo(() => {
    const map = {};
    departments.forEach((department) => {
      map[department.id] = department;
    });
    return map;
  }, [departments]);

  useEffect(() => {
    if (!departmentFilterName || !departments.length) return;
    const selectedName = departmentFilterName.trim().toLowerCase();
    const matchedDepartment = departments.find(
      (department) =>
        String(department?.name || "")
          .trim()
          .toLowerCase() === selectedName ||
        String(department?.code || "")
          .trim()
          .toLowerCase() === selectedName,
    );
    if (
      matchedDepartment?.id &&
      String(matchedDepartment.id) !== String(departmentFilter)
    ) {
      setDepartmentFilter(String(matchedDepartment.id));
    }
  }, [departmentFilterName, departments, departmentFilter]);

  const localUsersForDepartment = useMemo(
    () =>
      users.filter((user) =>
        userMatchesDepartment(user, departmentMap[form.departmentId]),
      ),
    [users, departmentMap, form.departmentId],
  );

  const usersForDepartment =
    String(departmentUsersDepartmentId) === String(form.departmentId)
      ? departmentUsers
      : localUsersForDepartment;

  const findUserDepartment = useCallback(
    (user) =>
      departments.find((department) => userMatchesDepartment(user, department)),
    [departments],
  );

  const findTaskDepartment = useCallback(
    (task, assignee) => {
      if (task?.department_id) {
        const savedDepartment = departmentMap[task.department_id];
        if (savedDepartment) return savedDepartment;
      }
      const taskDepartmentName =
        task?.department_name ||
        task?.department ||
        task?.dept_name ||
        task?.dept;
      const nameMatchedDepartment = departments.find((department) =>
        departmentMatchesName(department, taskDepartmentName),
      );
      if (nameMatchedDepartment) return nameMatchedDepartment;
      const assigneeDepartment = assignee ? findUserDepartment(assignee) : null;
      if (assigneeDepartment) return assigneeDepartment;
      return departments.find((department) =>
        departmentMatchesName(department, task?.role_name),
      );
    },
    [departmentMap, departments, findUserDepartment],
  );

  const loadRolesDepartmentsAndUsers = useCallback(async () => {
    try {
      const [rolesRes, departmentsRes, usersRes] = await Promise.all([
        fetchRoles(),
        fetchDepartments(),
        fetchUsers(),
      ]);
      setRoles(asArray(rolesRes));
      setDepartments(asArray(departmentsRes));
      setUsers(asArray(usersRes));
    } catch (e) {
    }
  }, []);

  useEffect(() => {
    loadRolesDepartmentsAndUsers();
  }, [loadRolesDepartmentsAndUsers]);

  const loadUsersForDepartment = useCallback(async (departmentId) => {
    if (!departmentId) return;
    setDepartmentUsersDepartmentId(departmentId);
    setDepartmentUsers([]);
    setDepartmentUsersLoading(true);
    try {
      const res = await fetchUsers({ department_id: departmentId, count: 100 });
      setDepartmentUsers(asArray(res));
    } catch (e) {
      displayToast("Failed to load users for this department", "error");
    } finally {
      setDepartmentUsersLoading(false);
    }
  }, []);

  const resolveDepartmentByAssignee = useCallback(
    async (assigneeId) => {
      if (!assigneeId || !departments.length) return null;
      const results = await Promise.all(
        departments.map(async (department) => {
          try {
            const res = await fetchUsers({
              department_id: department.id,
              count: 100,
            });
            const departmentUsersList = asArray(res);
            const hasAssignee = departmentUsersList.some(
              (user) => String(user.id) === String(assigneeId),
            );
            return hasAssignee ? department : null;
          } catch (e) {
            return null;
          }
        }),
      );
      return results.find(Boolean) || null;
    },
    [departments],
  );

  const loadCircularUsersForDepartment = useCallback(async (departmentId) => {
    const key = String(departmentId || "");
    if (!key || circularDepartmentRequestsRef.current[key]) return;
    circularDepartmentRequestsRef.current[key] = true;
    setCircularDepartmentLoadingById((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await fetchUsers({ department_id: departmentId, count: 100 });
      setCircularDepartmentUsersById((prev) => ({
        ...prev,
        [key]: asArray(res),
      }));
    } catch (e) {
      setCircularDepartmentUsersById((prev) => ({ ...prev, [key]: [] }));
    } finally {
      delete circularDepartmentRequestsRef.current[key];
      setCircularDepartmentLoadingById((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  const loadTasks = useCallback(
    async (silent = false) => {
      const requestId = ++taskRequestIdRef.current;
      if (!silent) setLoading(true);
      try {
        const apiStatus =
          activeView === VIEW_FOR_ME
            ? statusFilter === STATUS_AWAITING_APPROVAL
              ? STATUS_COMPLETED
              : statusFilter === STATUS_ALL
                ? undefined
                : statusFilter
            : statusFilter !== STATUS_ALL
              ? statusFilter
              : undefined;
        const params = {
          view: activeView,
          page,
          count: PAGE_SIZE,
          department_id: departmentFilter || undefined,
          status: apiStatus,
          search: search || undefined,
        };
        let res = await fetchTasks(params);
        const selectedDepartmentName =
          departmentMap[departmentFilter]?.name || departmentFilterName;
        if (
          departmentFilter &&
          selectedDepartmentName &&
          Array.isArray(res?.items) &&
          res.items.length === 0
        ) {
          const matchedRole = findRoleByDepartmentName(selectedDepartmentName);
          if (matchedRole?.id) {
            res = await fetchTasks({
              ...params,
              department_id: undefined,
              role_id: matchedRole.id,
            });
          }
        }
        if (requestId !== taskRequestIdRef.current) return;
        if (res?.error || res?.description) {
          displayToast(
            res?.error?.description ||
              res?.description ||
              "Failed to load tasks",
            "error",
          );
          return;
        }
        const items = Array.isArray(res?.items) ? res.items : [];
        const filteredItems =
          activeView === VIEW_FOR_ME
            ? items.filter((task) => {
                if (statusFilter === STATUS_PENDING) return !task.completed;
                if (statusFilter === STATUS_AWAITING_APPROVAL) {
                  return task.completed && !task.approved;
                }
                if (statusFilter === STATUS_COMPLETED) {
                  return task.completed && task.approved;
                }
                return true;
              })
            : items;
        const sortedItems = [...filteredItems].sort(
          (a, b) => taskSortStamp(b) - taskSortStamp(a),
        );

        setTasks(sortedItems);
        setTotal(
          activeView === VIEW_FOR_ME &&
            (statusFilter === STATUS_AWAITING_APPROVAL ||
              statusFilter === STATUS_COMPLETED)
            ? sortedItems.length
            : Number(res?.total_count) || 0,
        );
      } catch (e) {
        if (requestId === taskRequestIdRef.current) {
          displayToast("Failed to load tasks", "error");
        }
      } finally {
        if (!silent && requestId === taskRequestIdRef.current)
          setLoading(false);
      }
    },
    [
      activeView,
      page,
      departmentFilter,
      departmentFilterName,
      departmentMap,
      findRoleByDepartmentName,
      statusFilter,
      search,
    ],
  );

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!roles.length) return;
    if (!isAdmin && activeView === VIEW_ALL) {
      setActiveView(VIEW_FOR_ME);
      setStatusFilter(STATUS_PENDING);
      setPage(1);
    }
  }, [activeView, isAdmin, roles.length]);

  useEffect(() => {
    const nav = location.state;
    if (nav?.section === SECTION_CIRCULARS) {
      setActiveSection(SECTION_CIRCULARS);
      setCircularSearch("");
      setCircularPage(1);
      if (nav.circularId) selectCircular({ id: nav.circularId });
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (!nav?.view) return;
    setActiveSection(SECTION_TASKS);
    setActiveView(nav.view);
    setStatusFilter(nav.status || STATUS_PENDING);
    setFocusTaskId(nav.taskId || null);
    setDepartmentFilterName(nav.departmentName || "");
    setDepartmentFilter(
      nav.departmentId
        ? String(nav.departmentId)
        : nav.roleId
          ? String(nav.roleId)
          : "",
    );
    setPage(1);
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  useEffect(() => {
    if (!focusTaskId || loading) return undefined;
    const node = document.getElementById(`task-${focusTaskId}`);
    if (!node) return undefined;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
    const id = setTimeout(() => setFocusTaskId(null), 4000);
    return () => clearTimeout(id);
  }, [focusTaskId, loading, tasks]);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    if (!showModal) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (userPopupOpen) setUserPopupOpen(false);
      else if (roleMenuOpen) setRoleMenuOpen(false);
      else setShowModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal, userPopupOpen, roleMenuOpen]);

  useEffect(() => {
    if (!filterOpen) return undefined;
    const onClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [filterOpen]);

  useEffect(() => {
    if (!roleMenuOpen) return undefined;
    const onClick = (e) => {
      if (roleMenuRef.current && !roleMenuRef.current.contains(e.target)) {
        setRoleMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [roleMenuOpen]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => {
    if (!addMenuOpen) return undefined;
    const onClick = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setAddMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [addMenuOpen]);

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await fetchCircularUnreadCount();
      if (typeof res?.count === "number") setUnreadCount(res.count);
    } catch (e) {}
  }, []);

  const loadCirculars = useCallback(async (silent = false) => {
    if (!silent) setCircularsLoading(true);
    try {
      const res = await fetchCirculars();
      if (res?.error) {
        if (!silent) {
          displayToast(
            res?.error?.description || "Failed to load circulars",
            "error",
          );
        }
        return;
      }
      setCirculars(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      if (!silent) displayToast("Failed to load circulars", "error");
    } finally {
      if (!silent) setCircularsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCirculars();
    loadUnreadCount();
  }, [loadCirculars, loadUnreadCount]);

  useAutoRefresh(async () => {
    if (showModal) return;
    await loadTasks(true);
    await loadUnreadCount();
    if (circularMode === CIRCULAR_VIEW && !circularSaving) {
      await loadCirculars(true);
    }
  });

  const filteredCirculars = useMemo(() => {
    const q = circularSearch.trim().toLowerCase();
    if (!q) return circulars;
    return circulars.filter(
      (c) =>
        (c.title || "").toLowerCase().includes(q) ||
        (c.message || "").toLowerCase().includes(q) ||
        (c.creator_name || "").toLowerCase().includes(q),
    );
  }, [circulars, circularSearch]);

  const circularTotalPages = Math.max(
    1,
    Math.ceil(filteredCirculars.length / CIRCULAR_PAGE_SIZE),
  );
  const circularPageItems = filteredCirculars.slice(
    (circularPage - 1) * CIRCULAR_PAGE_SIZE,
    circularPage * CIRCULAR_PAGE_SIZE,
  );

  const recipientUsers = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.user_name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q),
    );
  }, [users, recipientSearch]);

  const circularUsersForDepartment = useCallback(
    (department) => {
      const key = String(department?.id || "");
      if (
        Object.prototype.hasOwnProperty.call(circularDepartmentUsersById, key)
      ) {
        return circularDepartmentUsersById[key];
      }
      return users.filter((user) => userMatchesDepartment(user, department));
    },
    [circularDepartmentUsersById, users],
  );

  useEffect(() => {
    if (
      circularMode === CIRCULAR_VIEW ||
      circularForm.recipientMode !== RECIPIENT_DEPARTMENTS
    ) {
      return;
    }
    departments.forEach((department) => {
      loadCircularUsersForDepartment(department.id);
    });
  }, [
    circularMode,
    circularForm.recipientMode,
    departments,
    loadCircularUsersForDepartment,
  ]);

  const selectedRecipients = useMemo(() => {
    const toRow = (u) => ({
      user_id: u.id,
      user_name: u.user_name,
      role_name: roleMap[userRoleId(u)]?.name || "",
      department_name: userDepartmentName(u),
      read: false,
    });
    if (circularForm.recipientMode === RECIPIENT_ALL) return users.map(toRow);
    if (circularForm.recipientMode === RECIPIENT_DEPARTMENTS) {
      const uniqueUsers = new Map();
      circularForm.departmentIds.forEach((id) => {
        circularUsersForDepartment(departmentMap[id]).forEach((user) => {
          uniqueUsers.set(String(user.id), user);
        });
      });
      return Array.from(uniqueUsers.values()).map(toRow);
    }
    return users
      .filter((u) => circularForm.userIds.includes(String(u.id)))
      .map(toRow);
  }, [users, roleMap, departmentMap, circularForm, circularUsersForDepartment]);

  const openCreateCircular = () => {
    setCircularForm(emptyCircularForm());
    setRecipientSearch("");
    setSelectedCircular(null);
    setCircularMode(CIRCULAR_CREATE);
    setActiveSection(SECTION_CIRCULARS);
  };

  const cancelCircularForm = () => {
    setCircularMode(CIRCULAR_VIEW);
    setCircularForm(emptyCircularForm());
  };

  useEffect(() => {
    if (circularMode === CIRCULAR_VIEW) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setCircularMode(CIRCULAR_VIEW);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [circularMode]);

  const loadCircularDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await fetchCircular(id);
      if (res?.error) {
        displayToast(
          res?.error?.description || "Failed to load circular",
          "error",
        );
        return null;
      }
      setSelectedCircular(res);
      return res;
    } catch (e) {
      displayToast("Failed to load circular", "error");
      return null;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const selectCircular = async (circular) => {
    setCircularMode(CIRCULAR_VIEW);
    const detail = await loadCircularDetail(circular.id);
    if (!detail) return;
    if (detail.is_recipient && !detail.read) {
      const res = await markCircularRead(circular.id);
      if (!res?.error) {
        setCirculars((prev) =>
          prev.map((c) => (c.id === circular.id ? { ...c, read: true } : c)),
        );
        setSelectedCircular((prev) => (prev ? { ...prev, read: true } : prev));
        loadUnreadCount();
      }
    }
  };

  const resolveTaskView = async (event) => {
    if (typeof event.assignedByMe === "boolean") {
      return event.assignedByMe ? VIEW_BY_ME : VIEW_FOR_ME;
    }
    const me = currentUserName().trim().toLowerCase();
    const matchesMe = (name) =>
      Boolean(name) && String(name).trim().toLowerCase() === me;
    if (matchesMe(event.assignerName)) return VIEW_BY_ME;
    if (matchesMe(event.assigneeName)) return VIEW_FOR_ME;
    try {
      const res = await fetchTasks({
        view: VIEW_FOR_ME,
        search: event.title,
        count: 1,
      });
      if (Array.isArray(res?.items) && res.items.length) return VIEW_FOR_ME;
    } catch (e) {}
    return VIEW_BY_ME;
  };

  const openCalendarEvent = async (event) => {
    if (event.type === "circular") {
      setActiveSection(SECTION_CIRCULARS);
      setCircularSearch("");
      setCircularPage(1);
      if (event.rawId) selectCircular({ id: event.rawId });
      return;
    }
    if (event.type === "todo") {
      setActiveSection(SECTION_TODOS);
      setTodoFocus({ title: event.title, nonce: Date.now() });
      return;
    }
    setActiveSection(SECTION_TASKS);
    setStatusFilter(STATUS_ALL);
    setSearchInput(event.title);
    setSearch(event.title);
    setPage(1);
    setActiveView(await resolveTaskView(event));
  };

  const openEditCircular = async (circular) => {
    const detail = (await loadCircularDetail(circular.id)) || circular;
    setCircularForm({
      title: detail.title || "",
      message: detail.message || "",
      date: parseApiDate(detail.circular_date),
      time: parseApiTime(detail.circular_time),
      allDay: !detail.circular_time,
      recipientMode: RECIPIENT_USERS,
      userIds: (detail.recipients || []).map((r) => String(r.user_id)),
      departmentIds: [],
    });
    setRecipientSearch("");
    setCircularMode(CIRCULAR_EDIT);
  };

  const setCircularField = (key, value) =>
    setCircularForm((prev) => ({ ...prev, [key]: value }));

  const toggleRecipientUser = (id) =>
    setCircularForm((prev) => ({
      ...prev,
      userIds: prev.userIds.includes(String(id))
        ? prev.userIds.filter((x) => x !== String(id))
        : [...prev.userIds, String(id)],
    }));

  const toggleRecipientDepartment = (id) =>
    setCircularForm((prev) => {
      const key = String(id);
      const selected = prev.departmentIds.includes(key);
      if (!selected) loadCircularUsersForDepartment(id);
      return {
        ...prev,
        departmentIds: selected
          ? prev.departmentIds.filter((x) => x !== key)
          : [...prev.departmentIds, key],
      };
    });

  const submitCircular = async (e) => {
    e.preventDefault();
    if (circularSaving) return;
    if (!circularForm.title.trim()) {
      displayToast("Circular title is required", "error");
      return;
    }
    if (!circularForm.message.trim()) {
      displayToast("Message is required", "error");
      return;
    }
    if (selectedRecipients.length === 0) {
      displayToast("Please choose at least one recipient", "error");
      return;
    }

    setCircularSaving(true);
    try {
      const payload = {
        title: circularForm.title.trim(),
        message: circularForm.message.trim(),
        circular_date: toApiDate(circularForm.date),
        circular_time: circularForm.allDay ? "" : toApiTime(circularForm.time),
        recipient_ids: selectedRecipients.map((r) => Number(r.user_id)),
      };
      const isEdit = circularMode === CIRCULAR_EDIT && selectedCircular?.id;
      const res = isEdit
        ? await updateCircular(selectedCircular.id, payload)
        : await createCircular(payload);
      if (res?.error) {
        displayToast(
          res?.error?.description || "Failed to save circular",
          "error",
        );
        return;
      }
      displayToast(isEdit ? "Circular updated" : "Circular sent", "success");
      setCircularMode(CIRCULAR_VIEW);
      setCircularForm(emptyCircularForm());
      setCircularPage(1);
      await loadCirculars();
      const detailId = isEdit ? selectedCircular.id : res?.id;
      if (detailId) loadCircularDetail(detailId);
    } catch (err) {
      displayToast("Failed to save circular", "error");
    } finally {
      setCircularSaving(false);
    }
  };

  const removeCircular = async () => {
    const target = circularToDelete;
    setCircularToDelete(null);
    if (!target) return;
    const res = await deleteCircular(target.id);
    if (res?.error) {
      displayToast(
        res?.error?.description || "Could not delete circular",
        "error",
      );
      return;
    }
    displayToast("Circular deleted", "success");
    if (selectedCircular?.id === target.id) setSelectedCircular(null);
    loadCirculars();
    loadUnreadCount();
  };

  const switchView = (view) => {
    if (view === activeView) return;
    setActiveView(view);
    setStatusFilter(view === VIEW_FOR_ME ? STATUS_PENDING : STATUS_ALL);
    setPage(1);
  };

  const changeDepartmentFilter = (departmentId) => {
    setDepartmentFilter(departmentId);
    setDepartmentFilterName(departmentMap[departmentId]?.name || "");
    setPage(1);
    setFilterOpen(false);
  };

  const changeStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const openAddModal = () => {
    setModalMode("add");
    setEditingId(null);
    setEditingTaskSnapshot(null);
    editDepartmentResolveRef.current += 1;
    submitLockRef.current = false;
    setForm(emptyForm());
    setRoleMenuOpen(false);
    setUserPopupOpen(false);
    setShowModal(true);
  };

  const openEditModal = (task) => {
    const assignee = users.find(
      (user) => String(user.id) === String(task.assignee_id),
    );
    const taskDepartment = findTaskDepartment(task, assignee);
    setModalMode("edit");
    setEditingId(task.id);
    setEditingTaskSnapshot(task);
    editDepartmentResolveRef.current += 1;
    submitLockRef.current = false;
    setForm({
      title: task.title || "",
      description: task.description || "",
      departmentId: taskDepartment?.id || task.department_id || "",
      departmentName:
        taskDepartment?.name ||
        task.department_name ||
        task.department ||
        task.dept_name ||
        "",
      roleId: task.role_id || "",
      roleName: task.role_name || "",
      assigneeId: task.assignee_id || "",
      assigneeName: task.assignee_name || "",
      date: task.deadline ? new Date(task.deadline) : new Date(),
      time: task.deadline ? new Date(task.deadline) : null,
      onHold: Boolean(task.on_hold),
      isCompleted: Boolean(task.completed),
    });
    setRoleMenuOpen(false);
    setUserPopupOpen(false);
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingTaskSnapshot(null);
    editDepartmentResolveRef.current += 1;
    setRoleMenuOpen(false);
    setUserPopupOpen(false);
  };

  useEffect(() => {
    if (
      !showModal ||
      modalMode !== "edit" ||
      form.departmentId ||
      !editingTaskSnapshot ||
      !departments.length
    ) {
      return;
    }
    const assignee = users.find(
      (user) => String(user.id) === String(editingTaskSnapshot.assignee_id),
    );
    const taskDepartment = findTaskDepartment(editingTaskSnapshot, assignee);
    if (taskDepartment?.id) {
      setForm((prev) => ({
        ...prev,
        departmentId: taskDepartment.id,
        departmentName: taskDepartment.name || prev.departmentName,
      }));
      return;
    }

    const resolveId = ++editDepartmentResolveRef.current;
    resolveDepartmentByAssignee(editingTaskSnapshot.assignee_id).then(
      (resolvedDepartment) => {
        if (
          resolveId !== editDepartmentResolveRef.current ||
          !resolvedDepartment?.id
        ) {
          return;
        }
        setForm((prev) => {
          if (prev.departmentId) return prev;
          return {
            ...prev,
            departmentId: resolvedDepartment.id,
            departmentName: resolvedDepartment.name || prev.departmentName,
          };
        });
      },
    );
  }, [
    showModal,
    modalMode,
    form.departmentId,
    editingTaskSnapshot,
    departments.length,
    users,
    findTaskDepartment,
    resolveDepartmentByAssignee,
  ]);

  const handleField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const selectDepartment = (department) => {
    setForm((prev) => ({
      ...prev,
      departmentId: department.id,
      departmentName: department.name,
      roleId: "",
      roleName: "",
      assigneeId: "",
      assigneeName: "",
    }));
    setRoleMenuOpen(false);
    setUserPopupOpen(true);
    loadUsersForDepartment(department.id);
  };

  const pickAssignee = (user) => {
    setForm((prev) => ({
      ...prev,
      assigneeId: user.id,
      assigneeName: user.user_name,
      roleId: userRoleId(user) || prev.roleId,
      roleName: roleMap[userRoleId(user)]?.name || prev.roleName,
    }));
    setUserPopupOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || submitLockRef.current) return;

    const title = form.title.trim();
    if (!title) {
      displayToast("Title is required", "error");
      return;
    }
    if (!form.departmentId) {
      displayToast("Please select a department", "error");
      return;
    }
    if (!form.assigneeId) {
      displayToast("Please choose a user to assign", "error");
      return;
    }

    submitLockRef.current = true;
    setSaving(true);
    try {
      const payload = {
        title,
        description: form.description.trim(),
        role_id: Number(form.roleId),
        role_name: form.roleName,
        department_id: Number(form.departmentId),
        department_name: form.departmentName,
        assignee_id: Number(form.assigneeId),
        assignee_name: form.assigneeName,
        on_hold: form.onHold,
      };
      if (!form.onHold) {
        payload.deadline = buildDeadline(form.date, form.time);
      }
      const res =
        modalMode === "edit"
          ? await updateTask(editingId, payload)
          : await createTask(payload);
      const isError =
        Boolean(res?.error) || res?.success === false || res?.status === false;
      if (isError) {
        displayToast(
          res?.error?.description ||
            res?.message ||
            res?.description ||
            "Failed to save task",
          "error",
        );
        return;
      }
      closeModal();
      displayToast(
        modalMode === "edit" ? "Task updated" : "Task assigned",
        "success",
      );
      if (modalMode === "add" && activeView !== VIEW_BY_ME) {
        switchView(VIEW_BY_ME);
      } else {
        loadTasks();
      }
    } catch (err) {
      displayToast("Failed to save task", "error");
    } finally {
      submitLockRef.current = false;
      setSaving(false);
    }
  };

  const toggleComplete = async (task) => {
    if (task.approved) return;
    if (task.on_hold) {
      displayToast("This task is on hold and cannot be completed", "error");
      return;
    }
    const next = !task.completed;
    const nextCompletedAt = next ? new Date().toISOString() : null;
    const previousCompletedAt = task.completed_at || null;
    setTasks((prev) =>
      prev.map((t) =>
        t.id === task.id
          ? { ...t, completed: next, completed_at: nextCompletedAt }
          : t,
      ),
    );
    const res = await updateTask(task.id, { completed: next });
    if (res?.error || res?.description) {
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                completed: !next,
                completed_at: previousCompletedAt,
              }
            : t,
        ),
      );
      displayToast("Could not update task", "error");
    }
  };

  const toggleApprove = async (task) => {
    if (!task.completed) return;
    const next = !task.approved;
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, approved: next } : t)),
    );
    const res = await updateTask(task.id, { approved: next });
    if (res?.error || res?.description) {
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, approved: !next } : t)),
      );
      displayToast(
        res?.error?.description || "Could not update approval",
        "error",
      );
      return;
    }
    displayToast(next ? "Task approved" : "Approval removed", "success");
  };

  const toggleHold = async (task) => {
    const next = !task.on_hold;
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, on_hold: next } : t)),
    );
    const res = await updateTask(task.id, { on_hold: next });
    if (res?.error || res?.description) {
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, on_hold: !next } : t)),
      );
      displayToast(
        res?.error?.description || "Could not update hold status",
        "error",
      );
      return;
    }
    displayToast(next ? "Task put on hold" : "Task activated", "success");
    if (!next) loadTasks();
  };

  const removeTask = async (task) => {
    const res = await deleteTask(task.id);
    if (res?.error || res?.description) {
      displayToast("Could not delete task", "error");
      return;
    }
    displayToast("Task deleted", "success");
    if (tasks.length === 1 && page > 1) {
      setPage((p) => p - 1);
    } else {
      loadTasks();
    }
  };

  const handleRemind = async (task) => {
    const res = await remindTask(task.id, task.assignee_id);
    if (res?.error || res?.description) {
      displayToast("Could not send reminder", "error");
      return;
    }
    displayToast(`Reminder sent to ${task.assignee_name}`, "success");
  };

  const handleRemindCircular = async (circular) => {
    const recipientIds = (circular.recipients || []).map((r) => r.user_id);
    if (recipientIds.length === 0) {
      displayToast("No recipients to remind", "error");
      return;
    }
    const res = await remindCircular(circular.id, recipientIds);
    if (res?.error || res?.description) {
      displayToast("Could not send reminder", "error");
      return;
    }
    displayToast("Reminder sent to all recipients", "success");
  };

  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString([], {
      day: "numeric",
      month: "short",
    });

  const relTime = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return fmtDate(iso);
  };

  return (
    <div className="tasks-page">
      <div className="tasks-toolbar">
        <div className="tasks-heading">
          <div className="tasks-heading-icon">
            <ClipboardList size={22} />
          </div>
          <div>
            <h1 className="tasks-title">Tasks</h1>
            <p className="tasks-subtitle">
              {total}{" "}
              {activeView === VIEW_BY_ME
                ? "assigned by you"
                : activeView === VIEW_ALL
                  ? "tasks across all users"
                  : "assigned to you"}
            </p>
          </div>
        </div>
        <div className="tasks-toolbar-actions">
          <div className="tasks-add-wrap" ref={addMenuRef}>
            <button
              className={`tasks-add-btn ${addMenuOpen ? "open" : ""}`}
              onClick={() => setAddMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={addMenuOpen}
            >
              <Plus size={18} />
              <span>Quick Actions</span>
              <ChevronDown
                size={16}
                className={`tasks-add-caret ${addMenuOpen ? "up" : ""}`}
              />
            </button>
            {addMenuOpen && (
              <ul className="tasks-add-menu" role="menu">
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setAddMenuOpen(false);
                      openAddModal();
                    }}
                  >
                    <UserPlus size={15} />
                    Assign Task
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setAddMenuOpen(false);
                      setActiveSection(SECTION_TODOS);
                      setTodoCreateSignal((n) => n + 1);
                    }}
                  >
                    <ClipboardList size={15} />
                    Add My Todo
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setAddMenuOpen(false);
                      openCreateCircular();
                    }}
                  >
                    <span className="circular-svg sm" aria-hidden="true" />
                    Create Circular
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="tasks-section-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === SECTION_CALENDAR}
          className={`tasks-section-tab ${
            activeSection === SECTION_CALENDAR ? "active" : ""
          }`}
          onClick={() => setActiveSection(SECTION_CALENDAR)}
        >
          Calendar
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === SECTION_TASKS}
          className={`tasks-section-tab ${
            activeSection === SECTION_TASKS ? "active" : ""
          }`}
          onClick={() => setActiveSection(SECTION_TASKS)}
        >
          Tasks
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === SECTION_TODOS}
          className={`tasks-section-tab ${
            activeSection === SECTION_TODOS ? "active" : ""
          }`}
          onClick={() => setActiveSection(SECTION_TODOS)}
        >
          Todos
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === SECTION_CIRCULARS}
          className={`tasks-section-tab ${
            activeSection === SECTION_CIRCULARS ? "active" : ""
          }`}
          onClick={() => setActiveSection(SECTION_CIRCULARS)}
        >
          Circulars
          {unreadCount > 0 && (
            <span className="tasks-section-badge">{unreadCount}</span>
          )}
        </button>
      </div>

      {activeSection === SECTION_CALENDAR ? (
        <CalendarView onOpenEvent={openCalendarEvent} />
      ) : activeSection === SECTION_TODOS ? (
        <TodosSection createSignal={todoCreateSignal} focus={todoFocus} />
      ) : activeSection === SECTION_CIRCULARS ? (
        <section className="circulars-board">
          <div className="circulars-head">
            <div>
              <h2 className="circulars-title">Circulars</h2>
              <p className="circulars-sub">
                Company announcements and communications
              </p>
            </div>
            <div className="circulars-head-actions">
              <div className="tasks-search">
                <Search size={15} />
                <input
                  type="text"
                  value={circularSearch}
                  onChange={(e) => {
                    setCircularSearch(e.target.value);
                    setCircularPage(1);
                  }}
                  placeholder="Search circulars…"
                />
                {circularSearch && (
                  <button
                    type="button"
                    className="tasks-search-clear"
                    onClick={() => setCircularSearch("")}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <button
                type="button"
                className="tasks-add-btn"
                onClick={openCreateCircular}
              >
                <Plus size={18} />
                <span>New Circular</span>
              </button>
            </div>
          </div>

          <div className="circulars-layout">
            <div className="circulars-list-pane">
              {circularsLoading ? (
                <div className="tasks-state">
                  <Loader2 className="tasks-spin" size={26} />
                  <p>Loading circulars…</p>
                </div>
              ) : circularPageItems.length === 0 ? (
                <div className="tasks-state">
                  <div className="tasks-empty-icon">
                    <span className="circular-svg xl" aria-hidden="true" />
                  </div>
                  <h3>No circulars yet</h3>
                  <p>Create a circular to announce something to your team.</p>
                  <button
                    className="tasks-add-btn ghost"
                    onClick={openCreateCircular}
                  >
                    <Plus size={18} />
                    <span>New Circular</span>
                  </button>
                </div>
              ) : (
                <ul className="circular-list">
                  {circularPageItems.map((c) => {
                    const unread = c.is_recipient && !c.read;
                    return (
                      <li
                        key={c.id}
                        className={`circular-item ${
                          selectedCircular?.id === c.id ? "active" : ""
                        } ${unread ? "unread" : ""}`}
                      >
                        <button
                          type="button"
                          className="circular-item-main"
                          onClick={() => selectCircular(c)}
                        >
                          <span className="circular-card-icon">
                            <span className="circular-svg" aria-hidden="true" />
                          </span>
                          <span className="circular-card-body">
                            <span className="circular-card-top">
                              <span className="circular-card-title">
                                {c.title}
                              </span>
                              <span
                                className={`circular-status ${
                                  c.is_creator ? "sent" : "received"
                                }`}
                              >
                                {c.is_creator ? "Sent" : "Received"}
                              </span>
                            </span>
                            <span className="circular-card-message">
                              {c.message}
                            </span>
                            <span className="circular-card-meta">
                              <span className="circular-meta-item">
                                <CalendarDays size={13} />
                                {fmtCircularDate(c.circular_date) || "—"}
                              </span>
                              <span className="circular-meta-item">
                                <Clock size={13} />
                                {c.circular_time
                                  ? fmtCircularTime(c.circular_time)
                                  : "All Day"}
                              </span>
                              <span className="circular-meta-item">
                                <Users size={13} />
                                To {c.recipient_count || 0} users
                              </span>
                              {unread && (
                                <span className="circular-meta-item unread-tag">
                                  <Mail size={13} />
                                  Unread
                                </span>
                              )}
                            </span>
                          </span>
                        </button>
                        {(c.can_edit || c.can_delete) && (
                          <div className="circular-item-actions">
                            {c.can_edit && (
                              <button
                                type="button"
                                className="task-action edit"
                                onClick={() => openEditCircular(c)}
                                aria-label="Edit circular"
                              >
                                <Pencil size={15} />
                              </button>
                            )}
                            {c.is_creator && (
                              <button
                                type="button"
                                className="task-action remind"
                                onClick={() => handleRemindCircular(c)}
                                title="Send reminder to recipients"
                                aria-label="Send reminder"
                              >
                                <Bell size={15} />
                              </button>
                            )}
                            {c.can_delete && (
                              <button
                                type="button"
                                className="task-action delete"
                                onClick={() => setCircularToDelete(c)}
                                aria-label="Delete circular"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {filteredCirculars.length > 0 && (
                <div className="circulars-footer">
                  <span className="tasks-page-info">
                    Showing {(circularPage - 1) * CIRCULAR_PAGE_SIZE + 1} to{" "}
                    {Math.min(
                      circularPage * CIRCULAR_PAGE_SIZE,
                      filteredCirculars.length,
                    )}{" "}
                    of {filteredCirculars.length} circulars
                  </span>
                  <div className="circulars-pager">
                    <button
                      type="button"
                      className="circular-page-btn"
                      onClick={() => setCircularPage((p) => Math.max(1, p - 1))}
                      disabled={circularPage <= 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from(
                      { length: circularTotalPages },
                      (_, i) => i + 1,
                    ).map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`circular-page-btn ${
                          p === circularPage ? "active" : ""
                        }`}
                        onClick={() => setCircularPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="circular-page-btn"
                      onClick={() =>
                        setCircularPage((p) =>
                          Math.min(circularTotalPages, p + 1),
                        )
                      }
                      disabled={circularPage >= circularTotalPages}
                      aria-label="Next page"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {circularMode !== CIRCULAR_VIEW && (
              <div
                className="task-modal-overlay"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) cancelCircularForm();
                }}
              >
                <div
                  className="circular-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Circular form"
                >
                  <div className="task-modal-head">
                    <div className="circular-modal-title">
                      <span className="circular-modal-icon">
                        <span className="circular-svg" aria-hidden="true" />
                      </span>
                      <div>
                        <h2>
                          {circularMode === CIRCULAR_EDIT
                            ? "Edit Circular"
                            : "Create New Circular"}
                        </h2>
                        <p>Send an announcement to selected users</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="task-modal-close"
                      onClick={cancelCircularForm}
                      disabled={circularSaving}
                      aria-label="Close"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <form className="circular-form" onSubmit={submitCircular}>
                    <label className="task-field">
                      <span>
                        Title <b>*</b>
                      </span>
                      <input
                        type="text"
                        value={circularForm.title}
                        onChange={(e) =>
                          setCircularField("title", e.target.value)
                        }
                        placeholder="Enter circular title"
                        maxLength={255}
                      />
                    </label>

                    <label className="task-field">
                      <span>
                        Message <b>*</b>
                      </span>
                      <textarea
                        rows={4}
                        value={circularForm.message}
                        onChange={(e) =>
                          setCircularField("message", e.target.value)
                        }
                        placeholder="Enter your message here…"
                        maxLength={5000}
                      />
                    </label>

                    <div className="task-field-row">
                      <div className="task-field">
                        <span>Date</span>
                        <DatePicker
                          selected={circularForm.date}
                          onChange={(d) => setCircularField("date", d)}
                          dateFormat="dd-MM-yyyy"
                          className="task-datepicker-input"
                          wrapperClassName="task-datepicker-wrapper"
                          popperProps={{ strategy: "fixed" }}
                          portalId="root-portal"
                          placeholderText="Select date"
                        />
                      </div>
                      <div className="task-field">
                        <span>Time</span>
                        <div className="circular-time-row">
                          <DatePicker
                            selected={circularForm.time}
                            onChange={(t) => setCircularField("time", t)}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="h:mm aa"
                            className="task-datepicker-input"
                            wrapperClassName="task-datepicker-wrapper"
                            popperProps={{ strategy: "fixed" }}
                            portalId="root-portal"
                            placeholderText="Select time"
                            disabled={circularForm.allDay}
                          />
                          <label className="circular-allday">
                            <input
                              type="checkbox"
                              checked={circularForm.allDay}
                              onChange={(e) => {
                                setCircularField("allDay", e.target.checked);
                                if (e.target.checked)
                                  setCircularField("time", null);
                              }}
                            />
                            <span>All Day</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="task-field">
                      <span>
                        Recipients <b>*</b>
                      </span>
                      <div className="circular-recipient-seg">
                        {[
                          {
                            key: RECIPIENT_USERS,
                            label: "Select Users",
                            Icon: Users,
                          },
                          {
                            key: RECIPIENT_DEPARTMENTS,
                            label: "By Department",
                            Icon: Building2,
                          },
                          {
                            key: RECIPIENT_ALL,
                            label: "All Users",
                            Icon: Send,
                          },
                        ].map(({ key, label, Icon }) => (
                          <button
                            key={key}
                            type="button"
                            className={`circular-recipient-tab ${
                              circularForm.recipientMode === key ? "active" : ""
                            }`}
                            onClick={() =>
                              setCircularField("recipientMode", key)
                            }
                          >
                            <Icon size={14} />
                            {label}
                          </button>
                        ))}
                      </div>

                      {circularForm.recipientMode === RECIPIENT_ALL ? (
                        <div className="circular-allusers-note">
                          <Users size={18} />
                          <p>
                            This circular will be sent to all {users.length}{" "}
                            users in the organisation.
                          </p>
                        </div>
                      ) : circularForm.recipientMode ===
                        RECIPIENT_DEPARTMENTS ? (
                        <div className="circular-picker">
                          <div className="circular-picker-list">
                            {departments.map((department) => {
                              const key = String(department.id);
                              const loading = Boolean(
                                circularDepartmentLoadingById[key],
                              );
                              const departmentUsersCount =
                                circularUsersForDepartment(department).length;
                              return (
                                <label
                                  key={department.id}
                                  className="circular-pick-row"
                                >
                                  <input
                                    type="checkbox"
                                    checked={circularForm.departmentIds.includes(
                                      key,
                                    )}
                                    onChange={() =>
                                      toggleRecipientDepartment(department.id)
                                    }
                                  />
                                  <span
                                    className="circular-pick-avatar"
                                    style={{
                                      background: roleColor(department.id),
                                    }}
                                  >
                                    {initialsOf(department.name)}
                                  </span>
                                  <span className="circular-pick-info">
                                    <span className="circular-pick-name">
                                      {department.name}
                                    </span>
                                    <span className="circular-pick-sub">
                                      {loading
                                        ? "Loading users..."
                                        : `${departmentUsersCount} users`}
                                    </span>
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="circular-picker">
                          <div className="circular-picker-search">
                            <Search size={15} />
                            <input
                              type="text"
                              value={recipientSearch}
                              onChange={(e) =>
                                setRecipientSearch(e.target.value)
                              }
                              placeholder="Search users…"
                            />
                          </div>
                          <div className="circular-picker-list">
                            {recipientUsers.length === 0 ? (
                              <div className="circular-picker-empty">
                                <Inbox size={22} />
                                <p>No users found</p>
                              </div>
                            ) : (
                              recipientUsers.map((u) => (
                                <label key={u.id} className="circular-pick-row">
                                  <input
                                    type="checkbox"
                                    checked={circularForm.userIds.includes(
                                      String(u.id),
                                    )}
                                    onChange={() => toggleRecipientUser(u.id)}
                                  />
                                  <span
                                    className="circular-pick-avatar"
                                    style={{
                                      background: roleColor(userRoleId(u)),
                                    }}
                                  >
                                    {initialsOf(u.user_name)}
                                  </span>
                                  <span className="circular-pick-info">
                                    <span className="circular-pick-name">
                                      {u.user_name}
                                      {(userDepartmentName(u) ||
                                        roleMap[userRoleId(u)]?.name) && (
                                        <em>
                                          {" "}
                                          (
                                          {userDepartmentName(u) ||
                                            roleMap[userRoleId(u)]?.name}
                                          )
                                        </em>
                                      )}
                                    </span>
                                    {u.email && (
                                      <span className="circular-pick-sub">
                                        {u.email}
                                      </span>
                                    )}
                                  </span>
                                </label>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="task-form-actions">
                      <button
                        type="button"
                        className="task-btn secondary"
                        onClick={cancelCircularForm}
                        disabled={circularSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="task-btn primary"
                        disabled={circularSaving}
                      >
                        {circularSaving ? (
                          <>
                            <Loader2 className="tasks-spin" size={16} /> Saving…
                          </>
                        ) : (
                          <>
                            <Send size={16} />
                            {circularMode === CIRCULAR_EDIT
                              ? "Update Circular"
                              : "Send Circular"}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <aside className="circulars-side-pane">
              <CircularPreview
                loading={detailLoading}
                circular={
                  circularMode === CIRCULAR_VIEW
                    ? {
                        title: selectedCircular?.title,
                        message: selectedCircular?.message,
                        date: selectedCircular?.circular_date,
                        time: selectedCircular?.circular_time,
                        author: selectedCircular?.creator_name,
                      }
                    : {
                        title: circularForm.title,
                        message: circularForm.message,
                        date: toApiDate(circularForm.date),
                        time: circularForm.allDay
                          ? ""
                          : toApiTime(circularForm.time),
                        author: currentUserName(),
                      }
                }
              />
              <RecipientsPanel
                recipients={
                  circularMode === CIRCULAR_VIEW
                    ? selectedCircular?.recipients || []
                    : selectedRecipients
                }
              />
            </aside>
          </div>
        </section>
      ) : (
        <>
          <div className="tasks-tabs-row">
            <div className="tasks-tabs">
              <button
                type="button"
                className={`tasks-tab ${activeView === VIEW_FOR_ME ? "active" : ""}`}
                onClick={() => switchView(VIEW_FOR_ME)}
              >
                <UserCheck size={16} />
                Assigned for you
              </button>
              <button
                type="button"
                className={`tasks-tab ${activeView === VIEW_BY_ME ? "active" : ""}`}
                onClick={() => switchView(VIEW_BY_ME)}
              >
                <Send size={16} />
                Assigned by you
              </button>
              {isAdmin && (
                <button
                  type="button"
                  className={`tasks-tab ${activeView === VIEW_ALL ? "active" : ""}`}
                  onClick={() => switchView(VIEW_ALL)}
                >
                  <Users size={16} />
                  All tasks
                </button>
              )}
            </div>
            {activeView === VIEW_FOR_ME && (
              <div className="tasks-hold-legend" aria-label="On hold indicator">
                <span className="tasks-hold-legend-swatch" aria-hidden="true" />
                <span>On Hold</span>
              </div>
            )}
          </div>

          <div className="tasks-filter-bar">
            <div className="task-filter-dropdown" ref={filterRef}>
              <button
                type="button"
                className={`task-filter-btn ${filterOpen ? "open" : ""}`}
                onClick={() => setFilterOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={filterOpen}
              >
                <ListFilter size={16} />
                <span className="task-filter-label">
                  {departmentFilter
                    ? departmentMap[departmentFilter]?.name ||
                      departmentFilterName ||
                      "Department"
                    : "All Departments"}
                </span>
                <ChevronDown
                  size={16}
                  className={`task-filter-caret ${filterOpen ? "up" : ""}`}
                />
              </button>
              {filterOpen && (
                <ul className="task-filter-menu" role="listbox">
                  <li>
                    <button
                      type="button"
                      className={`task-filter-option ${
                        departmentFilter === "" ? "active" : ""
                      }`}
                      onClick={() => changeDepartmentFilter("")}
                    >
                      <span className="task-filter-option-label">
                        <span className="board-dot all" />
                        All Departments
                      </span>
                    </button>
                  </li>
                  {departments.map((department) => (
                    <li key={department.id}>
                      <button
                        type="button"
                        className={`task-filter-option ${
                          String(departmentFilter) === String(department.id)
                            ? "active"
                            : ""
                        }`}
                        onClick={() =>
                          changeDepartmentFilter(String(department.id))
                        }
                      >
                        <span className="task-filter-option-label">
                          <span
                            className="board-dot"
                            style={{ background: roleColor(department.id) }}
                          />
                          {department.name}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="tasks-status-seg">
              {(activeView === VIEW_FOR_ME
                ? [
                    { key: STATUS_ALL, label: "All" },
                    { key: STATUS_PENDING, label: "Pending" },
                    {
                      key: STATUS_AWAITING_APPROVAL,
                      label: "Awaits for approval",
                    },
                    { key: STATUS_COMPLETED, label: "Completed" },
                  ]
                : [
                    { key: STATUS_ALL, label: "All" },
                    { key: STATUS_PENDING, label: "Pending" },
                    { key: STATUS_COMPLETED, label: "Completed" },
                  ]
              ).map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className={`tasks-seg-btn ${
                    statusFilter === s.key ? "active" : ""
                  }`}
                  onClick={() => changeStatusFilter(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="tasks-search">
              <Search size={15} />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search tasks…"
              />
              {searchInput && (
                <button
                  type="button"
                  className="tasks-search-clear"
                  onClick={() => setSearchInput("")}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="tasks-state">
              <Loader2 className="tasks-spin" size={30} />
              <p>Loading tasks…</p>
            </div>
          ) : tasks.length === 0 ? (
            <div className="tasks-state">
              <div className="tasks-empty-icon">
                <Inbox size={34} />
              </div>
              <h3>No tasks here</h3>
              <p>
                {activeView === VIEW_BY_ME
                  ? "You haven't assigned any tasks yet."
                  : activeView === VIEW_ALL
                    ? "No tasks found across users."
                    : "No tasks have been assigned to you."}
              </p>
              {activeView === VIEW_BY_ME && (
                <button className="tasks-add-btn ghost" onClick={openAddModal}>
                  <Plus size={18} />
                  <span>Assign Task</span>
                </button>
              )}
            </div>
          ) : (
            <>
              {activeView === VIEW_BY_ME && (
                <div className="tasks-list-head" aria-hidden="true">
                  <span className="tasks-list-head-status">Status</span>
                </div>
              )}

              <ul className="tasks-list">
                {tasks.map((task) => {
                  const badgeRoleId =
                    activeView === VIEW_FOR_ME
                      ? task.assigner_role_id || task.role_id
                      : task.role_id;
                  const badgeRoleName =
                    activeView === VIEW_FOR_ME
                      ? task.assigner_role_name || task.role_name
                      : task.role_name;
                  const statusLabel = task.approved
                    ? "Approved"
                    : task.completed
                      ? "Completed"
                      : task.on_hold
                        ? "On hold"
                        : "In Process";
                  const statusClass = task.approved
                    ? "approved"
                    : task.completed
                      ? "completed"
                      : task.on_hold
                        ? "onhold"
                        : "inprocess";
                  const color = roleColor(badgeRoleId);
                  const overdue =
                    task.deadline &&
                    !task.completed &&
                    new Date(task.deadline).getTime() < Date.now();
                  const canComplete = activeView === VIEW_FOR_ME;
                  const canManage = activeView === VIEW_BY_ME;
                  const canReview = activeView === VIEW_BY_ME;
                  const isApprovedCompleted = Boolean(task.approved);
                  return (
                    <li
                      key={task.id}
                      id={`task-${task.id}`}
                      className={`task-card ${task.completed ? "done" : ""} ${
                        isApprovedCompleted ? "approved-locked" : ""
                      } ${
                        String(focusTaskId) === String(task.id) ? "focused" : ""
                      } ${
                        task.on_hold
                          ? canComplete
                            ? "held-for-me"
                            : "held"
                          : ""
                      }`}
                    >
                      {canComplete ? (
                        <button
                          className={`task-check ${
                            task.completed ? "checked" : ""
                          } ${task.approved ? "approved" : ""}`}
                          onClick={() => toggleComplete(task)}
                          disabled={task.approved || task.on_hold}
                          title={
                            task.approved
                              ? "Approved by assigner"
                              : task.on_hold
                                ? "Task is on hold"
                                : task.completed
                                  ? "Mark as pending"
                                  : "Mark as done"
                          }
                          aria-label={
                            task.completed ? "Mark as pending" : "Mark as done"
                          }
                        >
                          {task.completed && <Check size={14} />}
                        </button>
                      ) : canReview && task.completed ? (
                        <button
                          className={`task-check approve ${
                            task.approved ? "checked" : ""
                          }`}
                          onClick={() => toggleApprove(task)}
                          disabled={task.approved}
                          title={
                            task.approved
                              ? "Already approved and completed"
                              : "Review done — approve & complete"
                          }
                          aria-label={
                            task.approved ? "Approved task" : "Approve task"
                          }
                        >
                          {task.approved && <BadgeCheck size={14} />}
                        </button>
                      ) : activeView === VIEW_ALL ? (
                        <span
                          className="task-check-placeholder"
                          aria-hidden="true"
                        />
                      ) : (
                        <span
                          className="task-check static"
                          title="Waiting for the assignee to complete"
                          aria-hidden="true"
                        />
                      )}

                      <div className="task-body">
                        <div className="task-meta task-meta-top">
                          {activeView === VIEW_BY_ME && (
                            <span className="task-meta-item assignee-item">
                              <UserCheck size={13} />
                              To: {task.assignee_name}
                            </span>
                          )}
                          <span className="task-meta-item created-time">
                            <Calendar size={13} />
                            {relTime(task.created_at)}
                          </span>
                          {task.deadline && (
                            <span
                              className={`task-meta-item deadline-time ${
                                overdue ? "overdue" : "upcoming"
                              }`}
                            >
                              <Clock size={13} />
                              {fmtDate(task.deadline)} {fmtTime(task.deadline)}
                            </span>
                          )}
                          {task.completed && task.completed_at && (
                            <span className="task-meta-item completed-time">
                              <Check size={13} />
                              Completed at: {fmtDate(task.completed_at)}{" "}
                              {fmtTime(task.completed_at)}
                            </span>
                          )}
                        </div>

                        <div className="task-top">
                          <h3 className="task-title">{task.title}</h3>
                          <div
                            className={`task-top-badges ${
                              activeView === VIEW_ALL
                                ? "all-view-people"
                                : canManage
                                  ? "with-status-column"
                                  : ""
                            }`}
                          >
                            {canManage && (
                              <span
                                className={`task-status-chip ${statusClass}`}
                              >
                                {statusLabel}
                              </span>
                            )}
                            {activeView === VIEW_ALL && (
                              <>
                                <span className="task-participant-chip assigner">
                                  <User size={12} />
                                  By:{" "}
                                  {fmtPersonAndRole(
                                    task.assigner_name,
                                    task.assigner_role_name,
                                  )}
                                </span>
                                <span className="task-participant-chip assignee">
                                  <UserCheck size={12} />
                                  To:{" "}
                                  {fmtPersonAndRole(
                                    task.assignee_name,
                                    task.role_name,
                                  )}
                                </span>
                              </>
                            )}
                            {activeView === VIEW_FOR_ME &&
                              task.assigner_name && (
                                <span className="task-assigner-badge">
                                  <User size={12} />
                                  By: {task.assigner_name}
                                </span>
                              )}
                            {activeView !== VIEW_ALL && badgeRoleName && (
                              <span
                                className="task-board-badge"
                                style={{
                                  color,
                                  background: `${color}1f`,
                                  borderColor: `${color}40`,
                                }}
                              >
                                {badgeRoleName}
                              </span>
                            )}
                          </div>
                        </div>

                        {(task.description ||
                          (activeView === VIEW_FOR_ME && task.approved)) && (
                          <div className="task-desc-row">
                            {task.description && (
                              <p className="task-desc">{task.description}</p>
                            )}
                            {activeView === VIEW_FOR_ME && task.approved && (
                              <span className="task-completed-inline">
                                <BadgeCheck size={13} /> Completed
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {canManage && (
                        <div className="task-actions">
                          {!task.approved && !task.completed && (
                            <button
                              className={`task-action hold ${
                                task.on_hold ? "active" : ""
                              }`}
                              onClick={() => toggleHold(task)}
                              title={
                                task.on_hold
                                  ? "Activate task (re-dates to today)"
                                  : "Put on hold (hidden from assignee)"
                              }
                              aria-label={
                                task.on_hold ? "Activate task" : "Hold task"
                              }
                            >
                              {task.on_hold ? (
                                <Play size={15} />
                              ) : (
                                <Pause size={15} />
                              )}
                            </button>
                          )}
                          {!task.approved && (
                            <button
                              className="task-action edit"
                              onClick={() => openEditModal(task)}
                              aria-label="Edit task"
                            >
                              <Pencil size={15} />
                            </button>
                          )}
                          <button
                            className="task-action remind"
                            onClick={() => handleRemind(task)}
                            title="Send reminder to assignee"
                            aria-label="Send reminder"
                          >
                            <Bell size={15} />
                          </button>
                          <button
                            className="task-action delete"
                            onClick={() => removeTask(task)}
                            aria-label="Delete task"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>

              {totalPages > 1 && (
                <div className="tasks-pagination">
                  <button
                    type="button"
                    className="tasks-page-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <ChevronLeft size={16} />
                    Prev
                  </button>
                  <span className="tasks-page-info">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="tasks-page-btn"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {showModal && (
        <div
          className="task-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="task-modal" role="dialog" aria-modal="true">
            <div className="task-modal-head">
              <h2>{modalMode === "edit" ? "Edit Task" : "Assign Task"}</h2>
              <button
                className="task-modal-close"
                onClick={closeModal}
                disabled={saving}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form className="task-form" onSubmit={handleSubmit}>
              <label className="task-field">
                <span>
                  Title <b>*</b>
                </span>
                <input
                  type="text"
                  value={form.title}
                  onChange={handleField("title")}
                  placeholder="What needs to be done?"
                  maxLength={255}
                  autoFocus
                />
              </label>

              <label className="task-field">
                <span>Description</span>
                <textarea
                  value={form.description}
                  onChange={handleField("description")}
                  placeholder="Add more details (optional)"
                  rows={3}
                  maxLength={5000}
                />
              </label>

              <label className="task-field">
                <span>Assigned By</span>
                <input
                  type="text"
                  className="task-readonly"
                  value={
                    modalMode === "edit"
                      ? form.assignerName || currentUserName()
                      : currentUserName()
                  }
                  readOnly
                  disabled
                />
              </label>

              <div className="task-field-row">
                <div className="task-field">
                  <span>
                    Department <b>*</b>
                  </span>
                  <div className="task-role-select" ref={roleMenuRef}>
                    <button
                      type="button"
                      className={`task-role-trigger ${
                        roleMenuOpen ? "open" : ""
                      }`}
                      onClick={() => setRoleMenuOpen((o) => !o)}
                    >
                      {form.departmentName ? (
                        <span className="task-role-value">
                          <span
                            className="board-dot"
                            style={{ background: roleColor(form.departmentId) }}
                          />
                          {form.departmentName}
                        </span>
                      ) : (
                        <span className="task-role-placeholder">
                          <Building2 size={15} />
                          Select a department
                        </span>
                      )}
                      <ChevronDown size={16} />
                    </button>
                    {roleMenuOpen && (
                      <ul className="task-role-menu">
                        {departments.length === 0 && (
                          <li className="task-role-empty">
                            No departments found
                          </li>
                        )}
                        {departments.map((department) => (
                          <li key={department.id}>
                            <button
                              type="button"
                              className={`task-role-option ${
                                String(form.departmentId) ===
                                String(department.id)
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => selectDepartment(department)}
                            >
                              <span
                                className="board-dot"
                                style={{ background: roleColor(department.id) }}
                              />
                              {department.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="task-field">
                  <span>
                    Assign To <b>*</b>
                  </span>
                  {form.assigneeName ? (
                    <div className="task-assignee-chip">
                      <span className="task-assignee-name">
                        <UserCheck size={14} />
                        {form.assigneeName}
                      </span>
                      <button
                        type="button"
                        className="task-assignee-change"
                        onClick={() => {
                          setUserPopupOpen(true);
                          loadUsersForDepartment(form.departmentId);
                        }}
                        disabled={!form.departmentId}
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="task-assignee-pick"
                      onClick={() => {
                        setUserPopupOpen(true);
                        loadUsersForDepartment(form.departmentId);
                      }}
                      disabled={!form.departmentId}
                    >
                      <User size={15} />
                      {form.departmentId
                        ? "Choose a user"
                        : "Select a department first"}
                    </button>
                  )}
                </div>
              </div>

              <div className="task-field-row">
                <div className="task-field">
                  <span>
                    Target Date {form.onHold && <em>(locked while on hold)</em>}
                  </span>
                  <DatePicker
                    selected={form.date}
                    onChange={(d) => setForm((prev) => ({ ...prev, date: d }))}
                    dateFormat="dd-MM-yyyy"
                    minDate={new Date()}
                    className="task-datepicker-input"
                    wrapperClassName="task-datepicker-wrapper"
                    popperProps={{ strategy: "fixed" }}
                    portalId="root-portal"
                    placeholderText="Select date"
                    disabled={form.onHold}
                  />
                </div>
                <div className="task-field">
                  <span>
                    Target Time <em>(optional)</em>
                  </span>
                  <DatePicker
                    selected={form.time}
                    onChange={(t) => setForm((prev) => ({ ...prev, time: t }))}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="task-datepicker-input"
                    wrapperClassName="task-datepicker-wrapper"
                    popperProps={{ strategy: "fixed" }}
                    portalId="root-portal"
                    placeholderText="Select time"
                    isClearable={!form.onHold}
                    disabled={form.onHold}
                  />
                </div>
              </div>

              {!form.isCompleted && (
                <button
                  type="button"
                  className={`task-hold-toggle ${form.onHold ? "on" : ""}`}
                  onClick={() =>
                    setForm((prev) => ({ ...prev, onHold: !prev.onHold }))
                  }
                >
                  <span className="task-hold-toggle-icon">
                    {form.onHold ? <Pause size={15} /> : <Play size={15} />}
                  </span>
                  <span className="task-hold-toggle-text">
                    <b>{form.onHold ? "On Hold" : "Active"}</b>
                    <small>
                      {form.onHold
                        ? "Hidden from assignee · activating re-dates it to today"
                        : "Visible to assignee · toggle to put on hold"}
                    </small>
                  </span>
                  <span
                    className={`task-hold-switch ${form.onHold ? "on" : ""}`}
                    aria-hidden="true"
                  >
                    <span className="task-hold-knob" />
                  </span>
                </button>
              )}

              <div className="task-form-actions">
                <button
                  type="button"
                  className="task-btn secondary"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="task-btn primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="tasks-spin" size={16} /> Saving…
                    </>
                  ) : modalMode === "edit" ? (
                    <>
                      <Check size={16} /> Save Changes
                    </>
                  ) : (
                    <>
                      <Send size={16} /> Assign Task
                    </>
                  )}
                </button>
              </div>
            </form>

            {userPopupOpen && (
              <div
                className="task-userpopup-overlay"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setUserPopupOpen(false);
                }}
              >
                <div className="task-userpopup" role="dialog" aria-modal="true">
                  <div className="task-userpopup-head">
                    <div className="task-userpopup-title">
                      <Users size={16} />
                      <span>
                        Users in {form.departmentName || "department"}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="task-modal-close"
                      onClick={() => setUserPopupOpen(false)}
                      aria-label="Close"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="task-userpopup-list">
                    {departmentUsersLoading ? (
                      <div className="task-userpopup-empty">
                        <Loader2 className="tasks-spin" size={26} />
                        <p>Loading users...</p>
                      </div>
                    ) : usersForDepartment.length === 0 ? (
                      <div className="task-userpopup-empty">
                        <Inbox size={26} />
                        <p>No users found under this department.</p>
                      </div>
                    ) : (
                      usersForDepartment.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          className={`task-user-row ${
                            String(form.assigneeId) === String(u.id)
                              ? "active"
                              : ""
                          }`}
                          onClick={() => pickAssignee(u)}
                        >
                          <span
                            className="task-user-avatar"
                            style={{ background: roleColor(form.departmentId) }}
                          >
                            {initialsOf(u.user_name)}
                          </span>
                          <span className="task-user-info">
                            <span className="task-user-name">
                              {u.user_name}
                            </span>
                            {u.email && (
                              <span className="task-user-email">{u.email}</span>
                            )}
                          </span>
                          {String(form.assigneeId) === String(u.id) && (
                            <Check size={16} className="task-user-check" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={Boolean(circularToDelete)}
        message={`Delete the circular "${circularToDelete?.title || ""}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={removeCircular}
        onCancel={() => setCircularToDelete(null)}
        onClose={() => setCircularToDelete(null)}
      />
    </div>
  );
};

/* ------------------------------ Circulars ------------------------------ */

const CircularPreview = ({ circular, loading }) => (
  <div className="circular-preview">
    <div className="circular-preview-head">Circular Preview</div>
    {loading ? (
      <div className="circular-preview-card">
        <Loader2 className="tasks-spin" size={24} />
      </div>
    ) : (
      <div className="circular-preview-card">
        <span className="circular-preview-icon">
          <span className="circular-svg lg" aria-hidden="true" />
        </span>
        <h3 className="circular-preview-title">
          {circular.title || "Circular title"}
        </h3>
        <div className="circular-preview-when">
          <span>
            <CalendarDays size={13} />
            {fmtCircularDate(circular.date) || "Date"}
          </span>
          <span className="circular-preview-dot" aria-hidden="true" />
          <span>
            <Clock size={13} />
            {circular.time ? fmtCircularTime(circular.time) : "All Day"}
          </span>
        </div>
        <p className="circular-preview-message">
          {circular.message || "Your message will appear here."}
        </p>
        <div className="circular-preview-from">
          <span>From</span>
          <b>{circular.author || "—"}</b>
        </div>
      </div>
    )}
  </div>
);

const RecipientsPanel = ({ recipients }) => {
  const total = recipients.length;
  const read = recipients.filter((r) => r.read).length;
  return (
    <div className="circular-recipients">
      <div className="circular-recipients-head">Recipients ({total})</div>
      <div className="circular-recipient-stats">
        <div className="circular-stat total">
          <b>{total}</b>
          <span>Total</span>
        </div>
        <div className="circular-stat read">
          <b>{read}</b>
          <span>Read</span>
        </div>
        <div className="circular-stat unread">
          <b>{total - read}</b>
          <span>Unread</span>
        </div>
      </div>
      <div className="circular-recipient-list">
        {total === 0 ? (
          <div className="circular-recipients-empty">
            <Mail size={20} />
            <p>No recipients selected yet.</p>
          </div>
        ) : (
          recipients.map((r) => (
            <div key={r.user_id} className="circular-recipient-row">
              <span className="circular-pick-avatar">
                {initialsOf(r.user_name)}
              </span>
              <span className="circular-pick-info">
                <span className="circular-pick-name">
                  {r.user_name}
                  {r.department_name || r.role_name ? (
                    <em> ({r.department_name || r.role_name})</em>
                  ) : null}
                </span>
                <span className="circular-pick-sub">
                  {r.read
                    ? `Read${r.read_at ? ` · ${fmtCircularDate(r.read_at)}` : ""}`
                    : "Not read yet"}
                </span>
              </span>
              <span className={`circular-read-dot ${r.read ? "read" : ""}`} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Tasks;
